import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import {
  fetchCheckoutStatus,
  finishSignup,
  studioAuth,
  formatApiError,
} from "../lib/studioApi";

/**
 * StudioFinishSignup — landing page Stripe redirects to after a successful
 * subscription checkout (?session_id=cs_...). We poll the backend for the
 * checkout's payment status. Once paid, we show an account-creation form so
 * the user picks their email/password and we provision the studio user
 * linked to the just-created Stripe subscription.
 */
export default function StudioFinishSignup() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = sp.get("session_id") || "";

  const [phase, setPhase] = useState("checking"); // checking | needs_account | submitting | error
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [artistName, setArtistName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Poll the session status up to ~30 attempts (60s) before giving up.
  useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      setError("Missing checkout session id. Did you arrive here via Stripe?");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const s = await fetchCheckoutStatus(sessionId);
        if (cancelled) return;
        setStatus(s);
        if (s.alreadyClaimed) {
          setPhase("error");
          setError(
            "This subscription is already linked to a Studio account. Please sign in instead.",
          );
          return;
        }
        if (s.paymentStatus === "paid" || s.status === "complete") {
          setPhase("needs_account");
          return;
        }
        if (s.status === "expired") {
          setPhase("error");
          setError("This checkout session expired. Please start over from the pricing page.");
          return;
        }
        if (attempts < 30) {
          setTimeout(tick, 2000);
        } else {
          setPhase("error");
          setError(
            "We couldn’t confirm your payment in time. If you were charged, please contact support.",
          );
        }
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        setError(
          formatApiError(
            err,
            "We couldn’t verify your payment right now. If you were charged, please contact support.",
          ),
        );
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim() || !email.trim() || !password) {
      toast.error("Please fill in name, email and password");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    setPhase("submitting");
    try {
      const { token, user } = await finishSignup({
        sessionId,
        name: name.trim(),
        email: email.trim(),
        password,
        artistName: artistName.trim() || name.trim(),
      });
      studioAuth.setToken(token);
      studioAuth.setUser(user);
      toast.success("Welcome to the Studio!");
      navigate("/studio", { replace: true });
    } catch (err) {
      setPhase("needs_account");
      toast.error(formatApiError(err, "Account creation failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f1e] text-white flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-7">
          <BrandLogo variant="yellow" className="h-10 mx-auto mb-2" />
          <div className="text-xs uppercase tracking-[0.3em] text-white/40">
            Welcome to the Studio
          </div>
        </div>

        {phase === "checking" && (
          <div
            data-testid="finish-checking"
            className="bg-[#10162a] border border-white/5 rounded-2xl p-8 text-center"
          >
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-paul-yellow mb-4" />
            <h1 className="text-xl font-bold mb-1">Confirming your payment…</h1>
            <p className="text-sm text-white/50">Hang tight, this only takes a few seconds.</p>
          </div>
        )}

        {phase === "error" && (
          <div
            data-testid="finish-error"
            className="bg-[#10162a] border border-red-500/30 rounded-2xl p-8 text-center"
          >
            <AlertTriangle className="w-10 h-10 mx-auto text-red-400 mb-3" />
            <h1 className="text-xl font-bold mb-1">Something went wrong</h1>
            <p className="text-sm text-white/60 mb-5">{error}</p>
            <Button
              onClick={() => navigate("/studio/login")}
              className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
              style={{ color: "#0b1535" }}
            >
              Back to login
            </Button>
          </div>
        )}

        {(phase === "needs_account" || phase === "submitting") && (
          <form
            data-testid="finish-form"
            onSubmit={submit}
            className="bg-[#10162a] border border-white/5 rounded-2xl p-7 shadow-[0_10px_40px_rgba(0,0,0,0.4)]"
          >
            <div className="flex items-start gap-3 bg-paul-yellow/10 border border-paul-yellow/30 rounded-xl px-4 py-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-paul-yellow flex-shrink-0 mt-0.5" />
              <div className="text-xs text-paul-yellow/90 leading-relaxed">
                <strong className="text-paul-yellow">Payment confirmed.</strong>{" "}
                Create your Studio account to unlock everything.
              </div>
            </div>

            <h1 className="text-2xl font-extrabold mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-paul-yellow" />
              One last step
            </h1>
            <p className="text-sm text-white/50 mb-6">
              We&apos;ll log you in to your Studio dashboard right after.
            </p>

            <Label className="text-xs uppercase tracking-wider text-white/50">Full name</Label>
            <Input
              data-testid="finish-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              className="h-11 mb-3 mt-1 bg-[#0b0f1e] border-white/10 text-white"
              disabled={busy}
              autoFocus
            />

            <Label className="text-xs uppercase tracking-wider text-white/50">Artist / stage name (optional)</Label>
            <Input
              data-testid="finish-artist"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              placeholder="How you appear in the studio"
              className="h-11 mb-3 mt-1 bg-[#0b0f1e] border-white/10 text-white"
              disabled={busy}
            />

            <Label className="text-xs uppercase tracking-wider text-white/50">Email</Label>
            <Input
              data-testid="finish-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-11 mb-3 mt-1 bg-[#0b0f1e] border-white/10 text-white"
              disabled={busy}
            />

            <Label className="text-xs uppercase tracking-wider text-white/50">Password</Label>
            <Input
              data-testid="finish-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="h-11 mb-6 mt-1 bg-[#0b0f1e] border-white/10 text-white"
              disabled={busy}
            />

            <Button
              data-testid="finish-submit"
              type="submit"
              disabled={busy}
              className="w-full h-12 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold text-base"
              style={{ color: "#0b1535" }}
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create my account"}
            </Button>

            {status?.amount > 0 && (
              <p className="text-[11px] text-white/30 text-center mt-4">
                Paid: ${Number(status.amount).toFixed(2)} {String(status.currency || "USD").toUpperCase()}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
